import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { IoMdClose } from 'react-icons/io';
import { User } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openForgotPasswordModal } from '../../stores/slices/commonSlice';
import LoginComponent from './loginComponent';
import RegisterComponent from './RegisterComponent';
import LoginWithGoogleComponent from './loginWithGoogle';
import { useEffect, useRef, useState } from 'react';
import { guestLoginAction } from '../../stores/actions/authActions';

const TAB_BASE =
  'flex-1 min-w-0 py-2.5 text-sm font-extrabold rounded-lg border-none transition-all duration-200 cursor-pointer';
const TAB_ACTIVE = 'bg-white text-gray-900 shadow-sm';
const TAB_INACTIVE = 'bg-transparent text-gray-500 hover:text-gray-700';

function GuestCheckoutScreen({
  onContinue,
  onLogin,
  continueHovered,
  onContinueMouseEnter,
  onContinueMouseLeave,
  isSubmitting,
}) {
  const continueButtonStyle = {
    width: '100%',
    height: '52px',
    backgroundColor: continueHovered && !isSubmitting ? '#1f2937' : '#000000',
    color: '#ffea00',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '12px',
    border: 'none',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.7 : 1,
    boxShadow: continueHovered && !isSubmitting ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
  };

  return (
    <div className="flex flex-col items-center px-2 pb-2 pt-1 text-center">
      <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 shadow-inner">
        <User size={32} strokeWidth={2.5} className="text-gray-400" />
      </div>

      <h3 className="text-lg font-black text-gray-900 mb-2">Continue as Guest</h3>

      <p className="mb-6 max-w-[95%] text-sm font-medium leading-relaxed text-gray-500">
       You can complete your purchase without creating an account or signing in. We just need your email to send the tickets on the next screen.
      </p>

      <button
        type="button"
        onClick={onContinue}
        disabled={isSubmitting}
        style={continueButtonStyle}
        onMouseEnter={onContinueMouseEnter}
        onMouseLeave={onContinueMouseLeave}
      >
        {isSubmitting ? 'Please wait...' : 'Continue to Checkout'}
      </button>

      <button
        type="button"
        onClick={onLogin}
        disabled={isSubmitting}
        className="mt-5 border-none bg-transparent p-0 text-sm font-semibold text-gray-600 underline underline-offset-2 transition-colors hover:text-gray-900 disabled:opacity-50"
      >
        Already have an account? Login
      </button>
    </div>
  );
}

export default function LoginSignUpModal() {
  const [value, setValue] = useState('login');
  const [closeHovered, setCloseHovered] = useState(false);
  const [continueHovered, setContinueHovered] = useState(false);
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  const dispatch = useDispatch();
  const openModal = useSelector((state) => state.common.openModal);
  const prevOpenModal = useRef(false);

  useEffect(() => {
    if (openModal && !prevOpenModal.current) {
      setValue('login');
      setGuestSubmitting(false);
      setLoginBusy(false);
    }
    prevOpenModal.current = openModal;
  }, [openModal]);

  // Any login/guest request in flight — drives the blocking overlay so the
  // buyer can't click, switch tabs, or close the modal mid-process.
  const busy = loginBusy || guestSubmitting;

  const handleClose = () => {
    if (busy) return;
    dispatch(closeModal());
  };

  const openForgotPassword = () => {
    dispatch(closeModal());
    dispatch(openForgotPasswordModal());
  };

  const handleGuestCheckout = async () => {
    if (guestSubmitting) return;
    setGuestSubmitting(true);
    try {
      await dispatch(guestLoginAction()).unwrap();
      dispatch(closeModal());
    } catch (error) {
      console.log(error);
    } finally {
      setGuestSubmitting(false);
    }
  };

  const headerTitle =
    value === 'register' ? 'Register' : value === 'guest' ? 'Guest' : 'Login';

  const closeButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    backgroundColor: closeHovered ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
    color: '#111827',
    fontSize: '1.5rem',
    transition: 'background-color 0.2s ease',
  };

  const isGuestView = value === 'guest';
  const isMobile = useMediaQuery('(max-width:767px)');

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      fullScreen={isMobile}
      className="login-signup-modal"
      PaperProps={{
        className: isMobile
          ? '!m-0 !w-full !max-w-full !h-[100dvh] !max-h-[100dvh] !rounded-none !shadow-none !overflow-hidden flex flex-col'
          : '!m-4 !w-[calc(100%-2rem)] !max-w-[480px] !max-h-[calc(100dvh-2rem)] !rounded-3xl !shadow-2xl !overflow-hidden flex flex-col',
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.55)' },
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col bg-white font-sans h-full" data-aos="fade-up">
        {/* Blocking overlay while a login/guest request is completing, so the
            buyer can't click anything (tabs, close, resubmit) mid-process. */}
        {busy && (
          <div
            className="absolute inset-0 z-[5] flex flex-col items-center justify-center gap-4 bg-white/85 backdrop-blur-sm px-6 text-center cursor-wait"
            role="alert"
            aria-live="assertive"
            aria-busy="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="h-12 w-12 rounded-full border-4 border-gray-200 border-t-[#ffea00] animate-spin"
              aria-hidden
            />
            <p className="m-0 text-base font-extrabold text-gray-900">
              {guestSubmitting ? 'Setting up guest checkout…' : 'Signing you in…'}
            </p>
            <p className="m-0 text-sm font-medium text-gray-500">
              Please don't close or refresh this page.
            </p>
          </div>
        )}
        {/* Yellow header */}
        <div className="flex shrink-0 items-center justify-between bg-[#ffea00] px-8 py-3.5  sm:py-4">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/apsession-won-icon.png"
              alt="Apsession"
              className="h-9 w-9 object-contain sm:h-10 sm:w-10"
            />
            <h2 className="m-0 text-2xl font-black text-black tracking-tight">{headerTitle}</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={closeButtonStyle}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            <IoMdClose />
          </button>
        </div>

        {/* White body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-4 pt-4 sm:pb-5 sm:pt-4">
          {/* Tab switcher */}
          <div className="mb-4 flex rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setValue('login')}
              className={`${TAB_BASE} ${value === 'login' ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => setValue('register')}
              className={`${TAB_BASE} ${value === 'register' ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              Register
            </button>
            <button
              type="button"
              onClick={() => setValue('guest')}
              className={`${TAB_BASE} ${value === 'guest' ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              Guest
            </button>
          </div>

          {/* Google login — above the form so it stays visible on Register too */}
          {!isGuestView && (
            <>
              <LoginWithGoogleComponent />

              <div className="mb-3 mt-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">OR</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
            </>
          )}

          {/* Tab content */}
          {value === 'login' && (
            <LoginComponent
              onForgotPassword={openForgotPassword}
              onBusyChange={setLoginBusy}
            />
          )}
          {value === 'register' && <RegisterComponent />}
          {value === 'guest' && (
            <GuestCheckoutScreen
              onContinue={handleGuestCheckout}
              onLogin={() => setValue('login')}
              continueHovered={continueHovered}
              onContinueMouseEnter={() => setContinueHovered(true)}
              onContinueMouseLeave={() => setContinueHovered(false)}
              isSubmitting={guestSubmitting}
            />
          )}

          {!isGuestView && (
            <button
              type="button"
              onClick={() => setValue('guest')}
              className="mt-4 w-full border-none bg-transparent p-0 pb-1 text-center text-sm font-semibold text-gray-600 underline underline-offset-2 transition-colors hover:text-gray-900"
            >
              Checkout as Guest
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
